import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function Brand() {
  return <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center"><Briefcase className="w-4 h-4 text-white" /></div><span className="font-display font-bold text-slate-900">Top-Recruit Careers</span></div>;
}

export function PublicCareers() {
  const [jobs, setJobs] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/public/jobs").then((r) => setJobs(r.data.items)).catch(() => setJobs([])); }, []);
  if (!jobs) return <PageLoader />;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4"><Brand /></header>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-1">Open Positions</h1>
        <p className="text-slate-500 mb-8">Explore current opportunities and apply in minutes.</p>
        {jobs.length ? (
          <div className="space-y-3" data-testid="public-jobs-list">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => navigate(`/careers/${j.id}`)} data-testid={`public-job-${j.id}`}
                className="w-full text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-brand/40 transition-all">
                <h3 className="font-semibold text-slate-900">{j.title}</h3>
                <p className="text-sm text-slate-500 flex items-center gap-3 mt-1">{j.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.location}</span>}{j.employment_type && <span>{j.employment_type}</span>}</p>
              </button>
            ))}
          </div>
        ) : <p className="text-slate-400" data-testid="public-jobs-empty">No open positions right now. Please check back soon.</p>}
      </div>
    </div>
  );
}

export function PublicJobApply() {
  const { jobId } = useParams();
  const refCode = new URLSearchParams(window.location.search).get("ref");
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resume, setResume] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", city: "", state: "", linkedin_url: "", referral_source: refCode ? `Job link ${refCode}` : "", answers: {} });
  useEffect(() => { api.get(`/public/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setErr(true)); }, [jobId]);
  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500">This position is no longer accepting applications.</div>;
  if (!job) return <PageLoader />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.mobile) return toast.error("Please complete the required fields");
    setSaving(true);
    try {
      let resume_token = null;
      if (resume) {
        const fd = new FormData(); fd.append("file", resume);
        const up = await api.post(`/public/jobs/${jobId}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_token = up.data.resume_token;
      }
      await api.post(`/public/jobs/${jobId}/apply`, { ...form, resume_token });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4"><Brand /></header>
      <div className="max-w-xl mx-auto px-6 py-10">
        {done ? (
          <div className="rounded-xl border border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="apply-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold text-slate-900">Application received</h2>
            <p className="text-slate-500 mt-1">Thank you for applying to {job.title}. Our team will be in touch.</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">{job.title}</h1>
            <p className="text-slate-500 mb-2">{job.account_name}</p>
            {job.description && <p className="text-sm text-slate-600 whitespace-pre-wrap mb-6 border-b border-slate-200 pb-6">{job.description}</p>}
            <h2 className="font-semibold text-slate-800 mb-3">Apply for this role</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">First Name *</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="apply-first" /></div>
                <div><Label className="text-xs font-semibold">Last Name *</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="apply-last" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="apply-email" /></div>
                <div><Label className="text-xs font-semibold">Mobile *</Label><Input value={form.mobile} onChange={set("mobile")} data-testid="apply-mobile" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">City</Label><Input value={form.city} onChange={set("city")} data-testid="apply-city" /></div>
                <div><Label className="text-xs font-semibold">State</Label><Input value={form.state} onChange={set("state")} data-testid="apply-state" /></div>
              </div>
              <div><Label className="text-xs font-semibold">LinkedIn URL</Label><Input value={form.linkedin_url} onChange={set("linkedin_url")} data-testid="apply-linkedin" /></div>
              <div>
                <Label className="text-xs font-semibold">Résumé <span className="text-slate-400 font-normal">(PDF, DOC or DOCX)</span></Label>
                <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setResume(e.target.files?.[0] || null)} data-testid="apply-resume"
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-brand hover:file:bg-brand/20" />
                {resume && <p className="text-xs text-slate-400 mt-1">{resume.name}</p>}
              </div>
              <div><Label className="text-xs font-semibold">How did you hear about us?</Label><Input value={form.referral_source} onChange={set("referral_source")} data-testid="apply-source" placeholder="LinkedIn, Referral, Website…" /></div>
              {(job.questions || []).map((q, i) => (
                <div key={i}><Label className="text-xs font-semibold">{q}</Label>
                  <Textarea rows={2} value={form.answers[q] || ""} onChange={(e) => setForm({ ...form, answers: { ...form.answers, [q]: e.target.value } })} data-testid={`apply-q-${i}`} /></div>
              ))}
              <Button onClick={submit} disabled={saving} className="w-full bg-brand hover:bg-brand-strong" data-testid="apply-submit">{saving ? "Submitting…" : "Submit Application"}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
